// Étape 10 — Éditeur de la « fiche mission » d'une prestation (admin)
import { useState, useRef, useEffect } from "react";
import { C, Btn, Input, Modal, FormField, Alert, prestationsApi } from "./shared";

const RichText = ({ value, onChange, testid }) => {
  const ref = useRef(null);
  useEffect(() => { if (ref.current && ref.current.innerHTML !== value) ref.current.innerHTML = value || ""; }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const cmd = (c) => { document.execCommand(c); onChange(ref.current.innerHTML); };
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 8 }}>
      <div style={{ display: "flex", gap: 4, padding: 6, borderBottom: `.5px solid ${C.border}`, background: C.gray2 }}>
        {[["bold", "G"], ["italic", "I"], ["insertUnorderedList", "• Liste"]].map(([c, l]) => (
          <button key={c} type="button" onMouseDown={(e) => { e.preventDefault(); cmd(c); }} data-testid={`${testid}-${c}`}
            style={{ border: `.5px solid ${C.border}`, background: "#fff", borderRadius: 4, padding: "2px 8px", fontSize: 12, cursor: "pointer", fontWeight: c === "bold" ? 700 : 400, fontStyle: c === "italic" ? "italic" : "normal" }}>{l}</button>
        ))}
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning data-testid={testid}
        onInput={() => onChange(ref.current.innerHTML)}
        style={{ minHeight: 110, padding: 10, fontSize: 13, lineHeight: 1.5, outline: "none" }} />
    </div>
  );
};

const ListEditor = ({ items, onChange, placeholder, testid }) => (
  <div style={{ display: "grid", gap: 6 }}>
    {items.map((it, i) => (
      <div key={i} style={{ display: "flex", gap: 6 }}>
        <Input value={it} onChange={(e) => onChange(items.map((x, j) => j === i ? e.target.value : x))} placeholder={placeholder} data-testid={`${testid}-${i}`} />
        <Btn small variant="red" onClick={() => onChange(items.filter((_, j) => j !== i))}>✕</Btn>
      </div>
    ))}
    <div><Btn small onClick={() => onChange([...items, ""])} data-testid={`${testid}-add`}>+ Ajouter</Btn></div>
  </div>
);

const Etapes = ({ etapes, onChange }) => (
  <div style={{ display: "grid", gap: 6 }}>
    {etapes.map((e, i) => {
      const set = (patch) => onChange(etapes.map((x, j) => j === i ? { ...x, ...patch } : x));
      const move = (d) => { const a = [...etapes]; [a[i], a[i + d]] = [a[i + d], a[i]]; onChange(a); };
      return (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "24px 1fr auto auto auto", gap: 6, alignItems: "center" }} data-testid={`fm-etape-${i}`}>
          <span style={{ fontSize: 11, color: C.muted, fontWeight: 700 }}>{i + 1}.</span>
          <Input value={e.libelle} onChange={(ev) => set({ libelle: ev.target.value })} data-testid={`fm-etape-libelle-${i}`} />
          <label style={{ fontSize: 11, display: "flex", gap: 4, alignItems: "center", whiteSpace: "nowrap" }}>
            <input type="checkbox" checked={!!e.documents_recus} onChange={(ev) => set({ documents_recus: ev.target.checked })} data-testid={`fm-etape-docs-${i}`} /> Documents reçus
          </label>
          {e.documents_recus ? (
            <Input type="number" value={e.delai_remise_jours ?? ""} placeholder="Délai j" style={{ width: 80 }}
              onChange={(ev) => set({ delai_remise_jours: ev.target.value === "" ? null : Number(ev.target.value) })} data-testid={`fm-etape-delai-${i}`} />
          ) : <span />}
          <div style={{ display: "flex", gap: 2 }}>
            <Btn small disabled={i === 0} onClick={() => move(-1)}>↑</Btn>
            <Btn small disabled={i === etapes.length - 1} onClick={() => move(1)}>↓</Btn>
            <Btn small variant="red" onClick={() => onChange(etapes.filter((_, j) => j !== i))}>✕</Btn>
          </div>
        </div>
      );
    })}
    <div><Btn small onClick={() => onChange([...etapes, { libelle: "", documents_recus: false, delai_remise_jours: null }])} data-testid="fm-etape-add">+ Ajouter une étape</Btn></div>
  </div>
);

const Documents = ({ groupes, onChange, prefix = "fm" }) => (
  <div style={{ display: "grid", gap: 10 }}>
    {groupes.map((g, i) => (
      <div key={i} style={{ border: `.5px solid ${C.border}`, borderRadius: 8, padding: 10 }} data-testid={`${prefix}-rubrique-${i}`}>
        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          <Input value={g.rubrique} placeholder="Rubrique (ex. Identité et famille)" onChange={(e) => onChange(groupes.map((x, j) => j === i ? { ...x, rubrique: e.target.value } : x))} data-testid={`${prefix}-rubrique-nom-${i}`} />
          <Btn small variant="red" onClick={() => onChange(groupes.filter((_, j) => j !== i))}>Supprimer</Btn>
        </div>
        <ListEditor items={g.items || []} placeholder="Document à fournir" testid={`${prefix}-doc-${i}`}
          onChange={(items) => onChange(groupes.map((x, j) => j === i ? { ...x, items } : x))} />
        <Input value={g.note || ""} placeholder="Mention sous la rubrique (facultatif)" style={{ marginTop: 6 }} data-testid={`${prefix}-note-${i}`}
          onChange={(e) => onChange(groupes.map((x, j) => j === i ? { ...x, note: e.target.value } : x))} />
      </div>
    ))}
    <div><Btn small onClick={() => onChange([...groupes, { rubrique: "", items: [], note: "" }])} data-testid={`${prefix}-rubrique-add`}>+ Ajouter une rubrique</Btn></div>
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 18 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: C.gold, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 8 }}>{title}</div>
    {children}
  </div>
);

export const FicheMissionModal = ({ prestation, prestations, onClose, onSaved }) => {
  const [f, setF] = useState({ titre: prestation.nom, sous_titre: "", deroulement: "", documents_intro: "", livrables: [], delai: "", documents: [], etapes: [],
    lettre_mission: true, prix_libre: false, prix_min: null, composants: [], carnet_inclus: null, mentions: "", ...(prestation.fiche_mission || {}) });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (patch) => setF({ ...f, ...patch });
  const save = async () => {
    setSaving(true); setErr("");
    try {
      const { id, ...rest } = prestation;
      await prestationsApi.update(id, { ...rest, fiche_mission: { ...f, a_relire: false } });
      onSaved(); onClose();
    } catch (e) { setErr(e?.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };
  const codes = prestations.filter(p => p.code !== prestation.code && ["paiement_unique", "abonnement_mensuel"].includes(p.type)).map(p => p.code);
  return (
    <Modal title={`Fiche mission — ${prestation.code}`} onClose={onClose} wide
      footer={<><Btn onClick={onClose}>Annuler</Btn><Btn variant="gold" disabled={saving} onClick={save} data-testid="fm-save">{saving ? "…" : "Enregistrer la fiche"}</Btn></>}>
      {err && <Alert type="error"><span>⚠</span><span>{err}</span></Alert>}
      {prestation.fiche_mission?.a_relire && <Alert type="info"><span>ℹ</span><span>Contenu de départ chargé automatiquement — à relire. L'enregistrement retire cette mention.</span></Alert>}
      <Section title="Présentation commerciale">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <FormField label="Titre"><Input value={f.titre} onChange={(e) => set({ titre: e.target.value })} data-testid="fm-titre" /></FormField>
          <FormField label="Sous-titre"><Input value={f.sous_titre} onChange={(e) => set({ sous_titre: e.target.value })} data-testid="fm-sous-titre" /></FormField>
        </div>
      </Section>
      <Section title="Déroulement"><RichText value={f.deroulement} onChange={(v) => set({ deroulement: v })} testid="fm-deroulement" /></Section>
      <Section title="Livrables"><ListEditor items={f.livrables} onChange={(v) => set({ livrables: v })} placeholder="Livrable" testid="fm-livrable" /></Section>
      <Section title="Délai"><Input value={f.delai} onChange={(e) => set({ delai: e.target.value })} data-testid="fm-delai" /></Section>
      <Section title="Documents à fournir">
        <FormField label="Mention en tête de liste (affichée au client)">
          <textarea value={f.documents_intro || ""} onChange={(e) => set({ documents_intro: e.target.value })} data-testid="fm-documents-intro"
            style={{ width: "100%", minHeight: 60, border: `1px solid ${C.border}`, borderRadius: 8, padding: 8, fontSize: 13, fontFamily: "inherit" }} />
        </FormField>
        <Documents groupes={f.documents} onChange={(v) => set({ documents: v })} />
      </Section>
      {(f.sujets || []).length > 0 && (
        <Section title="Documents selon le sujet (choisi par le consultant sur la ligne de la proposition)">
          {f.sujets.map((sj, k) => (
            <div key={sj} style={{ marginBottom: 12 }} data-testid={`fm-sujet-${k}`}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.teal, marginBottom: 6 }}>Sujet : {sj}</div>
              <Documents prefix={`fm-sujet-${k}`} groupes={(f.documents_par_sujet || {})[sj] || []}
                onChange={(v) => set({ documents_par_sujet: { ...(f.documents_par_sujet || {}), [sj]: v } })} />
            </div>
          ))}
        </Section>
      )}
      <Section title="Étapes de mission"><Etapes etapes={f.etapes} onChange={(v) => set({ etapes: v })} /></Section>
      <Section title="Règles">
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center", fontSize: 13 }}>
          <label><input type="checkbox" checked={!!f.lettre_mission} onChange={(e) => set({ lettre_mission: e.target.checked })} data-testid="fm-lettre-mission" /> Lettre de mission nécessaire</label>
          <label><input type="checkbox" checked={!!f.relecture_obligatoire} onChange={(e) => set({ relecture_obligatoire: e.target.checked })} data-testid="fm-relecture" /> Relecture admin obligatoire des livrables</label>
          <label><input type="checkbox" checked={!!f.prix_libre} onChange={(e) => set({ prix_libre: e.target.checked })} data-testid="fm-prix-libre" /> Prix libre avec minimum</label>
          {f.prix_libre && <Input type="number" value={f.prix_min ?? ""} placeholder="Minimum €" style={{ width: 120 }} onChange={(e) => set({ prix_min: e.target.value === "" ? null : Number(e.target.value) })} data-testid="fm-prix-min" />}
        </div>
      </Section>
      <Section title="Pack : prestations composantes">
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
          {f.composants.map((c, i) => (
            <span key={i} style={{ background: C.goldlt, color: C.golddm, borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600 }} data-testid={`fm-composant-${i}`}>
              {c} <button onClick={() => set({ composants: f.composants.filter((_, j) => j !== i) })} style={{ border: "none", background: "none", cursor: "pointer", color: C.golddm }}>✕</button>
            </span>
          ))}
          <select onChange={(e) => { if (e.target.value) set({ composants: [...f.composants, e.target.value] }); e.target.value = ""; }} data-testid="fm-composant-add" style={{ fontSize: 12, padding: 4 }}>
            <option value="">+ Ajouter un composant</option>
            {codes.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 13 }}>
          <span>Abonnement carnet inclus :</span>
          <select value={f.carnet_inclus?.code || ""} onChange={(e) => set({ carnet_inclus: e.target.value ? { code: e.target.value, duree_mois: f.carnet_inclus?.duree_mois || 12 } : null })} data-testid="fm-carnet" style={{ fontSize: 12, padding: 4 }}>
            <option value="">Aucun</option>
            {prestations.filter(p => p.type === "abonnement_mensuel").map(p => <option key={p.code} value={p.code}>{p.nom}</option>)}
          </select>
          {f.carnet_inclus && <><Input type="number" value={f.carnet_inclus.duree_mois} style={{ width: 80 }} onChange={(e) => set({ carnet_inclus: { ...f.carnet_inclus, duree_mois: Number(e.target.value) } })} data-testid="fm-carnet-duree" /> mois</>}
        </div>
      </Section>
      <Section title="Mentions particulières"><Input value={f.mentions} onChange={(e) => set({ mentions: e.target.value })} data-testid="fm-mentions" /></Section>
    </Modal>
  );
};
